/**
 * TeacherProfile — public page for /teachers/:teacherId
 *
 * Shows: teacher name, school, current credibility, count of chains they
 * inspired, and the chain list.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { GraduationCap, ChevronRight, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function TeacherProfile() {
  const { teacherId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/teachers/${teacherId}/attributions`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Teacher not found.');
      }
      setBusy(false);
    })();
  }, [teacherId]);

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: '#0A0A0A' }} data-testid="teacher-profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <GraduationCap size={16} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">TSOGN — Teacher Contributor</span>
        </div>

        {busy && (
          <div className="flex items-center gap-3 font-mono text-sm text-white/85 mt-6">
            <Loader2 size={16} className="animate-spin text-[#2F8A92]" />
            Loading…
          </div>
        )}
        {!busy && error && (
          <p className="font-mono text-sm text-white/85 mt-6">{error}</p>
        )}
        {!busy && data && (
          <div data-testid="teacher-profile-card">
            <h1 className="font-historical text-[#D4A84A] text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-2 mb-1">
              {data.teacher_name || 'TSOGN Teacher'}
            </h1>
            <p className="font-mono text-sm md:text-base text-white/85">
              {data.school_label || 'TSOGN contributing teacher'}
            </p>
            <div className="flex flex-wrap gap-3 my-5">
              <div className="border border-[#2F8A92]/20 rounded px-3 py-2">
                <div className="font-historical text-2xl text-[#2F8A92] font-light">{data.current_credibility_score}</div>
                <div className="font-hud text-[9px] tracking-widest text-white/65 uppercase">Credibility</div>
              </div>
              <div className="border border-[#D4A84A]/20 rounded px-3 py-2">
                <div className="font-historical text-2xl text-[#D4A84A] font-light">{data.chain_count}</div>
                <div className="font-hud text-[9px] tracking-widest text-white/65 uppercase">Chains inspired</div>
              </div>
            </div>

            {data.chain_count === 0 ? (
              <p className="font-mono text-xs md:text-sm text-white/65 mt-4">
                No published chains yet. Submitted lessons enter the methodology pipeline before they appear here.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.chains.map((c) => (
                  <li key={c.chain_id} className="border border-[#D4A84A]/15 rounded">
                    <Link
                      to={`/?chain=${c.chain_id}`}
                      className="block px-4 py-3 hover:bg-[#D4A84A]/05"
                      data-testid={`teacher-chain-${c.chain_id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-historical text-base md:text-lg text-white">{c.chain_title}</div>
                          <div className="font-mono text-[11px] text-white/55 mt-0.5">
                            {c.era || 'TSOGN chain'} · Inspired by lesson "{c.lesson_title}" ({c.grade_band} · {c.subject_area})
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-white/35 flex-shrink-0" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            <p className="font-mono text-[10px] text-white/35 mt-6 leading-relaxed">
              Teachers contribute lessons through the TSOGN Lessons Contest. Their names stay on the work, walked through the methodology with sources, contradictions, and primary citations.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
